import { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, addDoc, updateDoc, doc, onSnapshot, setDoc, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebaseConfig';

export interface Devolucion {
  id?: string;
  lotes: string[];
  comprador: string;
  vendedor: string;
  fechaSolicitud: string;
  motivo: string;
  montoPagado: number;
  penalizacion: number;
  porcentajePenalizacion: number;
  montoDevuelto: number;
  estado: 'pendiente' | 'aprobada' | 'rechazada';
  usuarioSolicita: string;
  usuarioAprueba?: string;
  fechaAprobacion?: string;
  datosTransferencia?: {
    numeroDocumento: string;
    transferencia?: string;
    boleta?: string;
    fecha: string;
    banco: string;
    beneficiario: string;
  };
  observaciones?: string;
  createdAt: string;
  updatedAt: string;
}

export function useDevoluciones() {
  const [devoluciones, setDevoluciones] = useState<Devolucion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Suscripción en tiempo real
  useEffect(() => {
    setLoading(true);
    const unsubscribe = onSnapshot(collection(db, 'devoluciones'), (snapshot) => {
      const data: Devolucion[] = [];
      snapshot.forEach((doc) => {
        data.push({ id: doc.id, ...doc.data() } as Devolucion);
      });
      setDevoluciones(data);
      setLoading(false);
    }, (err) => {
      setError('Error al sincronizar devoluciones');
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Fetch manual
  const fetchDevoluciones = useCallback(async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'devoluciones'));
      const data: Devolucion[] = [];
      querySnapshot.forEach((doc) => {
        data.push({ id: doc.id, ...doc.data() } as Devolucion);
      });
      setDevoluciones(data);
      setError(null);
    } catch (err) {
      setError('Error al cargar devoluciones');
    } finally {
      setLoading(false);
    }
  }, []);

  // Agregar devolución (estado pendiente)
  const addDevolucion = useCallback(async (devolucion: Omit<Devolucion, 'id' | 'createdAt' | 'updatedAt' | 'estado'>) => {
    try {
      const now = new Date().toISOString();
      const data: Devolucion = {
        ...devolucion,
        estado: 'pendiente',
        createdAt: now,
        updatedAt: now,
      };
      const docRef = await addDoc(collection(db, 'devoluciones'), data);
      return docRef.id;
    } catch (err) {
      setError('Error al agregar devolución');
      throw err;
    }
  }, []);

  // Aprobar devolución
  const aprobarDevolucion = useCallback(async (id: string, usuarioAprueba: string, datosTransferencia: Devolucion['datosTransferencia']) => {
    try {
      const now = new Date().toISOString();
      await updateDoc(doc(db, 'devoluciones', id), {
        estado: 'aprobada',
        usuarioAprueba,
        fechaAprobacion: now,
        datosTransferencia,
        updatedAt: now,
      });
    } catch (err) {
      setError('Error al aprobar devolución');
      throw err;
    }
  }, []);

  // Rechazar devolución
  const rechazarDevolucion = useCallback(async (id: string, usuarioAprueba: string, observaciones?: string) => {
    try {
      const now = new Date().toISOString();
      await updateDoc(doc(db, 'devoluciones', id), {
        estado: 'rechazada',
        usuarioAprueba,
        fechaAprobacion: now,
        observaciones,
        updatedAt: now,
      });
    } catch (err) {
      setError('Error al rechazar devolución');
      throw err;
    }
  }, []);

  return {
    devoluciones,
    loading,
    error,
    fetchDevoluciones,
    addDevolucion,
    aprobarDevolucion,
    rechazarDevolucion,
  };
}

// Aprobar devolución con impacto en lotes y movimientos
export const aprobarDevolucionConImpacto = async (
  devolucion: Devolucion,
  usuarioAprueba: string,
  datosTransferencia: Devolucion['datosTransferencia']
) => {
  const now = new Date().toISOString();
  // 1. Actualizar devolución
  await updateDoc(doc(db, 'devoluciones', devolucion.id!), {
    estado: 'aprobada',
    usuarioAprueba,
    fechaAprobacion: now,
    datosTransferencia,
    updatedAt: now,
  });
  // 2. Restaurar lotes
  for (const loteId of devolucion.lotes) {
    // Ver si es grupo (documento con idLotes) o individual
    const loteDoc = await getDoc(doc(db, 'lots', loteId));
    if (loteDoc.exists()) {
      const loteData = loteDoc.data();
      // Guardar snapshot previo en lotHistory
      const historyId = `${loteId}-${now.replace(/[:.]/g, '-')}`;
      await setDoc(doc(db, 'lotHistory', historyId), {
        lotId: loteId,
        ...loteData,
        deletedAt: now,
        action: 'devolución',
      });
      // Si es grupo (tiene idLotes y es array de más de 1)
      if (Array.isArray(loteData.idLotes) && loteData.idLotes.length > 1) {
        // Eliminar documento grupal
        await deleteDoc(doc(db, 'lots', loteId));
        // Restaurar cada lote individual
        for (const idInd of loteData.idLotes) {
          await setDoc(doc(db, 'lots', idInd), {
            id: idInd,
            status: 'disponible',
            seller: '',
            buyerName: '',
            buyerDPI: '',
            phoneNumber: '',
            depositSlip: '',
            receiptNumber: '',
            saleDate: '',
            paymentType: '',
            lotValue: loteData.lotValue || 0,
            totalValue: loteData.lotValue || 0,
            amountPaid: 0,
            numPayments: 0,
            payments: [],
            originalSeller: loteData.seller || '',
            createdAt: now,
            updatedAt: now,
          });
        }
      } else {
        // Individual: limpiar y dejar disponible
        await updateDoc(doc(db, 'lots', loteId), {
          status: 'disponible',
          seller: '',
          buyerName: '',
          buyerDPI: '',
          phoneNumber: '',
          depositSlip: '',
          receiptNumber: '',
          saleDate: '',
          paymentType: '',
          lotValue: loteData.lotValue || 0,
          totalValue: loteData.lotValue || 0,
          amountPaid: 0,
          numPayments: 0,
          payments: [],
          originalSeller: loteData.seller || '',
          updatedAt: now,
        });
      }
    }
  }
  // 3. Registrar movimiento de egreso
  // Construir referencia con información completa
  // Construir referencia usando numeroDocumento como principal (boleta mantenido para compatibilidad histórica)
  const referencia = datosTransferencia?.transferencia?.trim()
    ? `No. Doc.: ${datosTransferencia.numeroDocumento}, No. Autorización: ${datosTransferencia.transferencia}, Banco: ${datosTransferencia.banco}, Beneficiario: ${datosTransferencia.beneficiario}`
    : `No. Doc.: ${datosTransferencia?.numeroDocumento}, Banco: ${datosTransferencia?.banco}, Beneficiario: ${datosTransferencia?.beneficiario}`;
  
  // Preparar datos del movimiento sin campos undefined
  const movimientoData: any = {
    concepto: `Devolución a ${devolucion.comprador} por lote(s) ${devolucion.lotes.join(', ')}`,
    responsable: usuarioAprueba,
    fecha: now.split('T')[0],
    monto: devolucion.montoDevuelto,
    estado: 'Pagado',
    referencia: referencia,
    tipo: 'egreso',
    lotes: devolucion.lotes,
    comprador: devolucion.comprador,
    vendedor: devolucion.vendedor,
    automatic: true,
    origen: 'devolucion',
    numeroDocumento: datosTransferencia?.numeroDocumento?.trim(),
    createdAt: now,
    updatedAt: now,
  };
  
  // Solo agregar transferencia si tiene valor (Firestore no permite undefined)
  if (datosTransferencia?.transferencia?.trim()) {
    movimientoData.transferencia = datosTransferencia.transferencia.trim();
  }
  
  await addDoc(collection(db, 'movimientos'), movimientoData);
}; 